import { Individual, KnowledgeGraph } from "@semantical/modules/knowledge-graph";
import { SchemaIndividual } from "@semantical/modules/ontology-model";
import type { Node } from "@semantical/modules/semantical-core";
import { isIndividual } from "@semantical/modules/semantical-core";
import type { OntologyEnabledBoundedContext } from "@semantical/modules/workspace/OntologyEnabledBoundedContext";

export default class DiagramClass {
    x: number;
    y: number;
    width: number;
    height: number;
    modelElement: Node;  // Can be SchemaClass (TBox), Individual (ABox), or SchemaIndividual (TBox)
    hidden: boolean;  // Whether this class is hidden from the diagram view
    sourceGraphIRI?: string;  // The ontologyIRI of the source graph (for TBox individuals)

    constructor(modelElement: Node, x: number, y: number, width: number, height: number, hidden: boolean = false, sourceGraphIRI?: string) {
        this.modelElement = modelElement;
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.hidden = hidden;
        this.sourceGraphIRI = sourceGraphIRI;
    }

    /**
     * Check if this DiagramClass has a placeholder SchemaIndividual (missing class info).
     * This happens when the diagram was restored before the ontology was loaded.
     */
    isPlaceholder(): boolean {
        return this.modelElement instanceof SchemaIndividual &&
               this.modelElement.classUris.length === 0;
    }

    /**
     * Try to resolve a placeholder SchemaIndividual from the bounded context.
     * Call this after ontologies are loaded to update the modelElement with full data.
     * Returns true if the modelElement was updated.
     */
    resolveFromBoundedContext(boundedContext: OntologyEnabledBoundedContext): boolean {
        if (!this.isPlaceholder()) return false;

        const uri = this.modelElement.uri;

        // Try to find the individual in bounded context
        if (this.sourceGraphIRI) {
            const schema = boundedContext.getOntologyByIRI(this.sourceGraphIRI);
            if (schema) {
                const found = schema.getIndividualByUri(uri);
                if (found) {
                    this.modelElement = found;
                    return true;
                }
            }
        }

        // Search all ontologies
        for (const schema of boundedContext.getAllOntologies()) {
            const found = schema.getIndividualByUri(uri);
            if (found) {
                this.modelElement = found;
                return true;
            }
        }

        return false;
    }

    toJSON() {
        const isSchemaIndividual = this.modelElement instanceof SchemaIndividual;
        const isABoxIndividual = this.modelElement instanceof Individual;

        // Determine element type: schema-individual (TBox), individual (ABox), or class
        let elementType: string;
        if (isSchemaIndividual) {
            elementType = 'schema-individual';
        } else if (isABoxIndividual) {
            elementType = 'individual';
        } else {
            elementType = 'class';
        }

        // For schema-individual, also store classUris so stereotype can display before ontology loads
        const classUris = isSchemaIndividual && isIndividual(this.modelElement) && this.modelElement.classUris.length > 0
            ? this.modelElement.classUris
            : undefined;

        return {
            x: this.x,
            y: this.y,
            width: this.width,
            height: this.height,
            hidden: this.hidden || undefined,  // Only include if true
            modelElement: {
                href: this.modelElement.uri,
                type: elementType,
                sourceGraphIRI: this.sourceGraphIRI,  // Only present for schema individuals
                classUris  // Store class URIs for stereotype resolution
            }
        }
    }

    /**
     * Extract a readable label from a URI (last segment after # or /)
     */
    private static extractLabelFromUri(uri: string): string {
        const hashIndex = uri.lastIndexOf('#');
        if (hashIndex !== -1) return uri.substring(hashIndex + 1);
        const slashIndex = uri.lastIndexOf('/');
        if (slashIndex !== -1) return uri.substring(slashIndex + 1);
        return uri;
    }

    static fromJSON(json: any, knowledgeGraph: KnowledgeGraph, boundedContext?: OntologyEnabledBoundedContext): DiagramClass | null {
        const elementUri = json.modelElement.href;
        const elementType = json.modelElement.type;
        const sourceGraphIRI = json.modelElement.sourceGraphIRI;

        // Find the Node by its identifying URI
        let modelElement: Node | undefined;

        if (elementType === 'schema-individual') {
            // Try to look up from bounded context first (if ontology is loaded)
            if (boundedContext && sourceGraphIRI) {
                const schema = boundedContext.getOntologyByIRI(sourceGraphIRI);
                if (schema) {
                    modelElement = schema.getIndividualByUri(elementUri);
                }
            }
            // Fallback: search all ontologies in bounded context
            if (!modelElement && boundedContext) {
                for (const schema of boundedContext.getAllOntologies()) {
                    const found = schema.getIndividualByUri(elementUri);
                    if (found) {
                        modelElement = found;
                        break;
                    }
                }
            }
            // Final fallback: create a placeholder SchemaIndividual with just the URI
            // Use stored classUris if available for stereotype display
            if (!modelElement) {
                const label = DiagramClass.extractLabelFromUri(elementUri);
                const classUris = json.modelElement.classUris || [];
                modelElement = new SchemaIndividual(elementUri, label, classUris, '');
            }
        } else if (elementType === 'individual') {
            // Look in ABox individuals collection (uri = individualUri)
            modelElement = Array.from(knowledgeGraph.individuals?.values() ?? []).find(
                ind => ind.uri === elementUri
            );
        }

        // Note: For 'class' type (TBox editing), SchemaClass handling will be added in the future.

        if (!modelElement) {
            console.debug(`DiagramClass.fromJSON: Node not found for ${elementUri}`);
            return null;
        }

        return new DiagramClass(modelElement, json.x, json.y, json.width, json.height, json.hidden ?? false, sourceGraphIRI);
    }
}